/**
 * portalRegistry.js — single source for built-in + Firestore department portals.
 * Dashboard, notifications, and role pickers should use these helpers instead of
 * duplicating PORTAL_META / DEPTS arrays.
 */
import { PORTAL_CARDS } from '../constants/portalCards';

function hexAlpha(hex, alphaHex = '1f') {
  const h = (hex || '#6366f1').replace('#', '');
  if (h.length === 3) {
    return `#${h.split('').map(c => c + c).join('')}${alphaHex}`;
  }
  return `#${h}${alphaHex}`;
}

/** Visual + routing meta for a built-in PORTAL_CARDS entry */
export function metaFromPortalCard(card) {
  const color = card.color || '#6366f1';
  return {
    id: card.id,
    label: card.label,
    color,
    colorBg: hexAlpha(color, '1f'),
    colorBd: hexAlpha(color, '47'),
    path: card.path,
    collection: card.collection,
    desc: card.desc || '',
    builtIn: true,
  };
}

/** Visual + routing meta for a Firestore departments/{id} doc */
export function metaFromFirestoreDept(dept) {
  const color = dept.color || '#6366f1';
  return {
    id: dept.id,
    label: dept.name || dept.id,
    color,
    colorBg: hexAlpha(color, '1f'),
    colorBd: hexAlpha(color, '47'),
    path: `/dept/${dept.id}`,
    collection: 'dept_posts',
    deptId: dept.id,
    desc: dept.description || dept.subtitle || '',
    builtIn: false,
  };
}

/** Built-in portals that have a report feed (shown on dashboard). */
export function getBuiltInFeedPortals() {
  return PORTAL_CARDS.filter(p => p.collection && !p.noFeed);
}

/**
 * All dashboard-visible portals: built-in feed portals + admin-created departments.
 * Skips Firestore depts whose id collides with a built-in portal id.
 */
export function getDashboardPortals(customDepts = []) {
  const builtInIds = new Set(getBuiltInFeedPortals().map(p => p.id));
  const builtIn = getBuiltInFeedPortals().map(card => ({
    card,
    meta: metaFromPortalCard(card),
  }));
  const custom = (customDepts || [])
    .filter(d => d?.id && !builtInIds.has(d.id))
    .map(dept => ({
      card: {
        id: dept.id,
        label: dept.name,
        path: `/dept/${dept.id}`,
        collection: 'dept_posts',
        deptId: dept.id,
        desc: dept.description || dept.subtitle || '',
      },
      meta: metaFromFirestoreDept(dept),
    }));
  return [...builtIn, ...custom];
}

export function lookupPortalMeta(portalId, customDepts = []) {
  const card = PORTAL_CARDS.find(p => p.id === portalId);
  if (card?.collection) return metaFromPortalCard(card);
  const dept = (customDepts || []).find(d => d.id === portalId);
  if (dept) return metaFromFirestoreDept(dept);
  return null;
}

/** Roles derived from custom departments (for Team member role picker). */
export function rolesFromDepartments(depts = []) {
  const builtInIds = new Set(PORTAL_CARDS.map(p => p.id));
  return depts
    .filter(d => d?.id && !builtInIds.has(d.id))
    .map(d => ({
      id: d.id,
      label: d.name || d.id,
      color: d.color || '#64748b',
    }));
}
